import dayjs from './dayjs.config';
import i18nRaw, { t } from '../i18n';

// Workaround: com i18next v25 + TS 4.9 o tipo da instância não expõe `language`
// (parse errors em react-i18next/index.d.ts). Cast pra recuperar o campo.
const i18n = i18nRaw as typeof i18nRaw & { language: string };

export const normalizeLang = (lang: string): string => {
  const map: Record<string, string> = {
    pt: 'pt-BR',
    'pt-BR': 'pt-BR',
    'pt-PT': 'pt-BR', // usando pt-BR como base
    es: 'es-ES',
    'es-ES': 'es-ES',
    'es-AR': 'es-AR',
    en: 'en-US',
    'en-US': 'en-US',
    'en-GB': 'en-US', // fallback para US
    fr: 'fr-FR',
    'fr-FR': 'fr-FR',
    it: 'it-IT',
    'it-IT': 'it-IT',
    de: 'de-DE',
    'de-DE': 'de-DE',
  };

  return map[lang] || 'pt-BR';
};

const dateFormatConfig: Record<string, { format: string; regex: RegExp; placeholder: string }> = {
  'pt-BR': { format: 'DD/MM/YYYY', regex: /^\d{2}\/\d{2}\/\d{4}$/, placeholder: 'dd/mm/aaaa' },
  'es-ES': { format: 'DD/MM/YYYY', regex: /^\d{2}\/\d{2}\/\d{4}$/, placeholder: 'dd/mm/aaaa' },
  'es-AR': { format: 'DD/MM/YYYY', regex: /^\d{2}\/\d{2}\/\d{4}$/, placeholder: 'dd/mm/aaaa' },
  'fr-FR': { format: 'DD/MM/YYYY', regex: /^\d{2}\/\d{2}\/\d{4}$/, placeholder: 'jj/mm/aaaa' },
  'it-IT': { format: 'DD/MM/YYYY', regex: /^\d{2}\/\d{2}\/\d{4}$/, placeholder: 'gg/mm/aaaa' },
  'de-DE': { format: 'DD.MM.YYYY', regex: /^\d{2}\.\d{2}\.\d{4}$/, placeholder: 'tt.mm.jjjj' },
  'en-US': { format: 'MM/DD/YYYY', regex: /^\d{2}\/\d{2}\/\d{4}$/, placeholder: 'mm/dd/yyyy' },
};

const dateFormatConfigToDate: Record<string, { format: string; regex: RegExp; placeholder: string }> = {
  'pt-BR': { format: 'dd/MM/yyyy', regex: /^\d{2}\/\d{2}\/\d{4}$/, placeholder: 'dd/mm/aaaa' },
  'es-ES': { format: 'dd/MM/yyyy', regex: /^\d{2}\/\d{2}\/\d{4}$/, placeholder: 'dd/mm/aaaa' },
  'es-AR': { format: 'dd/MM/yyyy', regex: /^\d{2}\/\d{2}\/\d{4}$/, placeholder: 'dd/mm/aaaa' },
  'fr-FR': { format: 'dd/MM/yyyy', regex: /^\d{2}\/\d{2}\/\d{4}$/, placeholder: 'jj/mm/aaaa' },
  'it-IT': { format: 'dd/MM/yyyy', regex: /^\d{2}\/\d{2}\/\d{4}$/, placeholder: 'gg/mm/aaaa' },
  'de-DE': { format: 'dd.MM.yyyy', regex: /^\d{2}\.\d{2}\.\d{4}$/, placeholder: 'tt.mm.jjjj' },
  'en-US': { format: 'MM/dd/yyyy', regex: /^\d{2}\/\d{2}\/\d{4}$/, placeholder: 'mm/dd/yyyy' },
};

export const getDateFormatByLanguage = (lang = 'pt-BR') => {
  const normalized = normalizeLang(lang);
  return dateFormatConfig[normalized] || dateFormatConfig['pt-BR'];
};

export const getDateFormatByLanguageToDate = (lang = 'pt-BR') => {
  const normalized = normalizeLang(lang);
  return dateFormatConfigToDate[normalized] || dateFormatConfig['pt-BR'];
};

export const formatDateLocalized = (date: string | Date) => {
  const lang = normalizeLang(i18n.language);
  let format = 'DD/MM/YYYY';

  switch (lang) {
    case 'en-US':
      format = 'MM/DD/YYYY';
      break;
    case 'de-DE':
      format = 'DD.MM.YYYY';
      break;
    default:
      format = 'DD/MM/YYYY';
      break;
  }

  return dayjs(date).locale(lang).format(format);
};

export const formatedDateToDate = (dateString: string): Date | null => {
  if (!dateString) {
    return null;
  }
  const lang = normalizeLang(i18n.language);

  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day); // Mês começa d
  return dayjs(date).locale(lang).toDate();
};

export const formatDatetimeLocalized = (date: string | Date) => {
  const lang = normalizeLang(i18n.language);
  let format = 'DD/MM/YYYY HH:mm:ss';

  switch (lang) {
    case 'en-US':
      format = 'YYYY/MM/DD HH:mm:ss';
      break;
    case 'de-DE':
      format = 'DD.MM.YYYY HH:mm:ss';
      break;
    default:
      format = 'DD/MM/YYYY HH:mm:ss';
      break;
  }

  return dayjs(date).locale(lang).format(format);
};

export const formatShortDate = (date?: string | Date) => {
  if (!date) return '';
  const lang = normalizeLang(i18n.language);
  switch (lang) {
    case 'en-US':
      return dayjs(date).format('MM/DD');
    case 'de-DE':
      return dayjs(date).format('DD.MM');
    default:
      return dayjs(date).format('DD/MM');
  }
};

export const formatShortTime = (date?: string | Date) => {
  if (!date) return '';
  return dayjs(date).format('HH:mm');
};

export const formatDayOfWeek = (date?: string | Date) => {
  if (!date) return '';
  const day = dayjs(date).locale('en').format('ddd').toLowerCase();
  return t(`calendar.short_days_of_week.${day}`);
};

export const formatMonthName = (date?: string | Date) => {
  if (!date) return '';
  const month = dayjs(date).locale('en').format('MMM').toLowerCase(); // 'jan', 'feb' etc
  return t(`calendar.short_month.${month}`);
};

export const toBackendDate = (displayDate: string) => {
  const { format } = getDateFormatByLanguage(i18n.language);
  return dayjs(displayDate, format).format('YYYY-MM-DD');
};

export const formatShortMonthDay = (date: string | Date) => {
  const lang = normalizeLang(i18n.language);
  return dayjs(date).locale(lang).format('DD MMM'); // ex: "09 Apr"
};

export const formatTime = (date: string | Date) => {
  return dayjs(date).format('HH:mm');
};
